import type { QuoteRoute } from "./quote.ts";

export function formatQuote(route: QuoteRoute) {
  const total = route.estimatedTime.total;
  const mins = Math.ceil(total / 60);
  const feeSum =
    route.fees?.reduce((s, f) => s + f.totalFee, 0) ?? 0;

  return {
    estimatedOutput: route.expectedBuyAmount,
    estimatedOutputMin: route.expectedBuyAmountMaxSlippage,
    fees: feeSum > 0 ? `$${feeSum.toFixed(2)}` : "minimal",
    estimatedTime: mins > 0 ? `~${mins} min` : "< 1 min",
    providers: route.providers.join(", "),
    slippageBps: route.totalSlippageBps,
  };
}

export interface AssetPair {
  asset: string;
  label: string;
  chainId: string;
  /** ERC-20 contract address — undefined for native assets like ETH */
  tokenAddress?: `0x${string}`;
  /** Minimum transferable amount in this asset's units */
  minAmount: number;
  /** Maximum transferable amount (beta cap) in this asset's units */
  maxAmount: number;
}

export const USDC_BASE_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913" as const;

/** Supported source assets for entering the shielded pool. */
export const ENTER_ASSETS: AssetPair[] = [
  { asset: "BASE.ETH", label: "ETH (Base)", chainId: "8453", minAmount: 0.003, maxAmount: 1 },
  { asset: `BASE.USDC-${USDC_BASE_ADDRESS}`, label: "USDC (Base)", chainId: "8453", tokenAddress: USDC_BASE_ADDRESS, minAmount: 8, maxAmount: 2500 },
];

/** Supported destination assets for exiting the shielded pool. */
export const EXIT_ASSETS: AssetPair[] = [
  { asset: "BASE.ETH", label: "ETH (Base)", chainId: "8453", minAmount: 0.003, maxAmount: 1 },
  { asset: `BASE.USDC-${USDC_BASE_ADDRESS}`, label: "USDC (Base)", chainId: "8453", tokenAddress: USDC_BASE_ADDRESS, minAmount: 8, maxAmount: 2500 },
];

/** ZEC asset identifier for SwapKit API. */
export const ZEC_ASSET = "ZEC.ZEC";

const CHAIN_TO_TRACK_ID: Record<string, string> = {
  BASE: "8453",
  ETH: "1",
  ARB: "42161",
  AVAX: "43114",
  BSC: "56",
  MATIC: "137",
  OP: "10",
  BTC: "bitcoin",
  ZEC: "zcash",
  LTC: "litecoin",
  DOGE: "dogecoin",
  BCH: "bitcoincash",
};

/** Extract the SwapKit track-API chain ID from a SwapKit asset identifier (e.g. "BASE.ETH" → "8453"). */
export function trackChainId(asset: string): string {
  const chain = asset.split(".")[0];
  return CHAIN_TO_TRACK_ID[chain] ?? chain.toLowerCase();
}
